import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Phone, PhoneOff, Mic, MicOff, Minimize2, Maximize2, PhoneForwarded, X, Pause, Play, Grid3X3 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { toast } from "sonner";
import { useTwilio } from "@/contexts/TwilioContext";
import { useIsMobile } from "@/hooks/use-mobile";
import divusLogoWhite from '@/assets/divus-logo-white.png';
import { supabase } from "@/integrations/supabase/client";

interface ActiveCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactName?: string;
  contactPhone: string;
  direction: "inbound" | "outbound";
  callRecordId?: string | null;
  isCallActive: boolean;
  onEndCall: () => void;
  onToggleMute: () => boolean;
  onTransfer?: () => void;
  onTransferParked?: () => void;
  isMuted?: boolean;
  errorMessage?: string | null;
  parkedClientName?: string | null;
  parkedClientPhone?: string | null;
}

function ActiveCallModal({
  isOpen,
  onClose,
  contactName,
  contactPhone,
  direction,
  callRecordId,
  isCallActive,
  onEndCall,
  onToggleMute,
  onTransfer,
  onTransferParked,
  isMuted = false,
  errorMessage = null,
  parkedClientName = null,
  parkedClientPhone = null,
}: ActiveCallModalProps) {
  const isMobile = useIsMobile();
  const { isParked, holdCall, resumeCall, sendDtmf } = useTwilio();
  const [duration, setDuration] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [localIsMuted, setLocalIsMuted] = useState(isMuted);
  const [callNotes, setCallNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isProcessingPark, setIsProcessingPark] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isOpen && isCallActive) {
      interval = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isOpen, isCallActive]);

  useEffect(() => {
    if (!isOpen) {
      setDuration(0);
      setLocalIsMuted(false);
      setIsMinimized(false);
      setCallNotes("");
      setIsSavingNotes(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setLocalIsMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    if (!isCallActive && isMinimized) {
      setIsMinimized(false);
    }
  }, [isCallActive, isMinimized]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleEndCall = () => {
    onEndCall();
  };

  const handleToggleMute = () => {
    const newMutedState = onToggleMute();
    setLocalIsMuted(newMutedState);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleParkToggle = async () => {
    if (isProcessingPark) return;

    setIsProcessingPark(true);

    try {
      if (isParked) {
        console.log("📞 Resuming call from park...");
        await resumeCall();
        toast.success("Cliente retomado!");
      } else {
        console.log("🅿️ Parking call...");
        await holdCall();
      }
    } catch (error) {
      console.error("❌ Park/Resume error:", error);
      toast.error(`Erro ao ${isParked ? "retomar" : "colocar em espera"} a chamada`);
    } finally {
      setIsProcessingPark(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!callRecordId) {
      toast.error("ID da chamada não encontrado");
      return;
    }

    if (!callNotes.trim()) {
      toast.error("Digite uma nota antes de salvar");
      return;
    }

    setIsSavingNotes(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error("User not authenticated");

      const { data: existingCall } = await supabase.from("calls").select("notes").eq("id", callRecordId).single();

      const { data: appUser } = await supabase
        .from("app_users")
        .select("id, first_name, last_name, display_name")
        .eq("auth_user_id", user.id)
        .single();

      const userName =
        appUser?.display_name ||
        `${appUser?.first_name || ""} ${appUser?.last_name || ""}`.trim() ||
        user.email ||
        "Unknown User";

      let existingNotes = [];
      if (existingCall?.notes) {
        try {
          existingNotes = typeof existingCall.notes === "string" ? JSON.parse(existingCall.notes) : existingCall.notes;
          if (!Array.isArray(existingNotes)) existingNotes = [];
        } catch {
          existingNotes = [];
        }
      }

      const newNote = {
        text: callNotes.trim(),
        created_by: userName,
        created_by_id: appUser?.id || user.id,
        created_date: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("calls")
        .update({ notes: JSON.stringify([...existingNotes, newNote]) })
        .eq("id", callRecordId);

      if (error) throw error;

      toast.success("Nota salva com sucesso");
      onClose();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Erro ao salvar nota");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleCloseWithoutNotes = () => {
    onClose();
  };

  const initials =
    contactName
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "??";

  const dialPadButtons = [
    { digit: "1", letters: "" },
    { digit: "2", letters: "ABC" },
    { digit: "3", letters: "DEF" },
    { digit: "4", letters: "GHI" },
    { digit: "5", letters: "JKL" },
    { digit: "6", letters: "MNO" },
    { digit: "7", letters: "PQRS" },
    { digit: "8", letters: "TUV" },
    { digit: "9", letters: "WXYZ" },
    { digit: "*", letters: "" },
    { digit: "0", letters: "+" },
    { digit: "#", letters: "" },
  ];

  // Minimized state - positioned above mobile nav bar
  if (isMinimized) {
    return (
      <div className={`fixed ${isMobile ? 'bottom-24' : 'bottom-6'} right-4 md:right-6 z-50`}>
        <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-2xl shadow-2xl p-4 min-w-[280px] border-2 border-primary/20">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12 border-2 border-primary-foreground/20">
              <AvatarFallback className="text-lg bg-primary-foreground/20 text-primary-foreground font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-semibold text-sm truncate">{contactName || contactPhone}</p>
              <div className="flex items-center gap-2">
                {isCallActive ? (
                  <>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <p className="text-sm font-mono">{formatDuration(duration)}</p>
                    {isParked && <span className="text-xs">🎵</span>}
                  </>
                ) : (
                  <p className="text-xs">Finalizada - {formatDuration(duration)}</p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {isCallActive ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleToggleMute}
                    className={`w-9 h-9 rounded-full ${
                      localIsMuted
                        ? "bg-destructive/20 text-destructive hover:bg-destructive/30"
                        : "bg-primary-foreground/10 hover:bg-primary-foreground/20"
                    }`}
                  >
                    {localIsMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleMinimize}
                    className="w-9 h-9 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleEndCall}
                    className="w-9 h-9 rounded-full bg-destructive/20 hover:bg-destructive/30 text-destructive"
                  >
                    <PhoneOff className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMinimize}
                  className="w-9 h-9 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20"
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile fullscreen version for active call
  if (isMobile && isCallActive) {
    return (
      <div className="fixed inset-0 z-[100] bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Header with logo and minimize */}
        <div className="flex items-center justify-between px-6 pt-4">
          <img src={divusLogoWhite} alt="Divus Legal Group" className="h-7 w-auto opacity-90" />
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
              {direction === "inbound" ? "Entrada" : "Saída"}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMinimize}
              className="w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <Minimize2 className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Contact info */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl scale-150" />
            <Avatar className="relative w-28 h-28 border-4 border-white/20 shadow-2xl">
              <AvatarFallback className="text-3xl bg-gradient-to-br from-primary to-primary/80 text-white font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="absolute -top-1 -right-1">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500 rounded-full animate-ping" />
                <div className="relative bg-green-500 rounded-full p-2">
                  <Phone className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mt-6">{contactName || "Contato"}</h2>
          <p className="text-white/70 mt-1">{contactPhone}</p>

          {/* Duration */}
          <div className="mt-6 flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm">
            <div className={`w-3 h-3 rounded-full animate-pulse ${isParked ? "bg-yellow-400" : "bg-green-400"}`} />
            <p className="text-2xl font-bold text-white font-mono">{formatDuration(duration)}</p>
            {isParked && <span className="ml-2 text-yellow-400">🎵 Em espera</span>}
          </div>

          {/* Parked client banner */}
          {isParked && parkedClientName && parkedClientPhone && (
            <div className="mt-4 w-full max-w-xs px-4 py-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🎵</span>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-yellow-200">{parkedClientName} aguardando</p>
                    <p className="text-xs text-yellow-300/80">{parkedClientPhone}</p>
                  </div>
                </div>
                {onTransferParked && (
                  <Button
                    size="sm"
                    onClick={onTransferParked}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white gap-1"
                  >
                    <PhoneForwarded className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Keypad */}
          {showKeypad && (
            <div className="mt-6 w-full max-w-[280px]">
              <div className="grid grid-cols-3 gap-3">
                {dialPadButtons.map((button) => (
                  <Button
                    key={button.digit}
                    variant="outline"
                    className="h-16 flex flex-col items-center justify-center text-xl font-semibold bg-white/10 border-white/20 text-white hover:bg-white/20 active:scale-95 transition-all"
                    onClick={() => sendDtmf(button.digit)}
                  >
                    <span>{button.digit}</span>
                    {button.letters && <span className="text-xs text-white/60">{button.letters}</span>}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom controls */}
        <div className="px-6 pb-6 pt-4">
          {/* Control buttons row */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              className={`w-14 h-14 rounded-full transition-all ${
                localIsMuted
                  ? "bg-red-500/20 text-red-400"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
              onClick={handleToggleMute}
              disabled={isProcessingPark || isParked}
            >
              {localIsMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className={`w-14 h-14 rounded-full transition-all ${
                isParked
                  ? "bg-yellow-500/20 text-yellow-400"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
              onClick={handleParkToggle}
              disabled={isProcessingPark}
            >
              {isProcessingPark ? (
                <div className="animate-spin w-6 h-6 border-2 border-current border-t-transparent rounded-full" />
              ) : isParked ? (
                <Play className="w-6 h-6" />
              ) : (
                <Pause className="w-6 h-6" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className={`w-14 h-14 rounded-full transition-all ${
                showKeypad
                  ? "bg-primary/30 text-primary"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
              onClick={() => setShowKeypad(!showKeypad)}
              disabled={isProcessingPark || isParked}
            >
              <Grid3X3 className="w-6 h-6" />
            </Button>

            {onTransfer && (
              <Button
                variant="ghost"
                size="icon"
                className="w-14 h-14 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
                onClick={onTransfer}
                disabled={isProcessingPark || isParked}
              >
                <PhoneForwarded className="w-6 h-6" />
              </Button>
            )}
          </div>

          {/* End call button */}
          <div className="flex justify-center">
            <Button
              variant="destructive"
              size="icon"
              className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-2xl ring-4 ring-red-500/30 transition-all active:scale-95"
              onClick={handleEndCall}
              disabled={isProcessingPark}
            >
              <PhoneOff className="w-8 h-8" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop dialog version
  return (
    <Dialog open={isOpen && !isMinimized} onOpenChange={!isCallActive ? handleCloseWithoutNotes : handleMinimize}>
      <DialogContent className={`border-0 bg-background shadow-2xl px-6 ${isMobile ? 'max-w-full h-full rounded-none' : 'sm:max-w-lg'}`}>
        <VisuallyHidden>
          <DialogTitle>Chamada Ativa</DialogTitle>
          <DialogDescription>Controles da chamada em andamento com {contactName || contactPhone}</DialogDescription>
        </VisuallyHidden>

        {!isCallActive ? (
          <div className="flex flex-col py-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-bold text-foreground">Chamada Encerrada</h3>
              <Button variant="ghost" size="icon" onClick={handleCloseWithoutNotes} className="h-8 w-8 rounded-full">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="text-center space-y-2 pb-4">
              <div className="flex items-center justify-center gap-3">
                <Avatar className="w-16 h-16 border-2 border-border">
                  <AvatarFallback className="text-lg bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="font-semibold text-foreground">{contactName || "Contato"}</p>
                  <p className="text-sm text-muted-foreground">{contactPhone}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-1">Duração: {formatDuration(duration)}</p>
                </div>
              </div>
            </div>

            {/* Error message if present */}
            {errorMessage && (
              <div className="w-full px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive font-medium text-center">
                  ⚠️ {errorMessage}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Adicionar notas da chamada</label>
              <Textarea
                placeholder="Digite suas observações sobre esta chamada..."
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                className="min-h-[120px]"
                autoFocus
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={handleCloseWithoutNotes} className="flex-1" disabled={isSavingNotes}>
                Fechar sem salvar
              </Button>
              <Button onClick={handleSaveNotes} className="flex-1" disabled={isSavingNotes || !callNotes.trim()}>
                {isSavingNotes ? "Salvando..." : "Salvar e Fechar"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-6 space-y-5 max-h-[85vh] overflow-y-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full blur-2xl scale-150" />
              <Avatar className="relative w-32 h-32 border-4 border-background shadow-xl ring-4 ring-primary/10">
                <AvatarFallback className="text-3xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -top-2 -right-2">
                <div className="relative">
                  <div className="absolute inset-0 bg-green-500 rounded-full animate-ping" />
                  <div className="relative bg-green-500 rounded-full p-2">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-2xl font-bold text-foreground tracking-tight">{contactName || "Contato"}</h3>
              <p className="text-base text-muted-foreground font-medium">{contactPhone}</p>
              <Badge variant="secondary" className="mt-2 bg-primary/10 text-primary border-primary/20">
                {direction === "inbound" ? "Chamada Recebida" : "Chamada Efetuada"}
              </Badge>
            </div>

            {/* Banner de cliente em espera */}
            {isParked && parkedClientName && parkedClientPhone && (
              <div className="w-full px-4 py-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/30 rounded-lg">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🎵</span>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                        {parkedClientName} aguardando
                      </p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-300">
                        {parkedClientPhone}
                      </p>
                    </div>
                  </div>
                  {onTransferParked && (
                    <Button
                      size="sm"
                      onClick={onTransferParked}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white gap-2"
                    >
                      <PhoneForwarded className="w-4 h-4" />
                      Transferir
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 min-h-[56px] px-8 py-4 rounded-full bg-gradient-to-r from-primary/10 to-primary/20 border border-primary/20">
              <div className={`w-3 h-3 rounded-full animate-pulse ${isParked ? "bg-yellow-500" : "bg-primary"}`} />
              <p className="text-3xl font-bold tabular-nums text-primary">{formatDuration(duration)}</p>
              {isParked && <span className="ml-2 text-sm text-yellow-600 font-medium">🎵 Em espera</span>}
            </div>

            {showKeypad && (
              <div className="w-full max-w-[280px] mx-auto">
                <div className="grid grid-cols-3 gap-3">
                  {dialPadButtons.map((button) => (
                    <Button
                      key={button.digit}
                      variant="outline"
                      className="h-16 flex flex-col items-center justify-center text-xl font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
                      onClick={() => sendDtmf(button.digit)}
                    >
                      <span>{button.digit}</span>
                      {button.letters && (
                        <span className="text-xs text-muted-foreground">
                          {button.letters}
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 pt-2">
              <Button
                variant="outline"
                size="icon"
                className={`w-14 h-14 rounded-full border-2 transition-all duration-300 shadow-lg ${
                  localIsMuted
                    ? "bg-destructive/10 border-destructive/30 hover:bg-destructive/20 text-destructive"
                    : "bg-background/80 border-border hover:bg-accent hover:border-primary/30"
                }`}
                onClick={handleToggleMute}
                disabled={isProcessingPark || isParked}
              >
                {localIsMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </Button>

              <Button
                variant="outline"
                size="icon"
                className={`w-14 h-14 rounded-full border-2 transition-all duration-300 shadow-lg ${
                  isParked
                    ? "bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 text-yellow-600"
                    : "bg-background/80 border-border hover:bg-accent hover:border-primary/30"
                }`}
                onClick={handleParkToggle}
                disabled={isProcessingPark}
              >
                {isProcessingPark ? (
                  <div className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
                ) : isParked ? (
                  <Play className="w-5 h-5" />
                ) : (
                  <Pause className="w-5 h-5" />
                )}
              </Button>

              <Button
                variant="outline"
                size="icon"
                className={`w-14 h-14 rounded-full border-2 transition-all duration-300 shadow-lg ${
                  showKeypad
                    ? "bg-primary/10 border-primary/30 hover:bg-primary/20 text-primary"
                    : "bg-background/80 border-border hover:bg-accent hover:border-primary/30"
                }`}
                onClick={() => setShowKeypad(!showKeypad)}
                disabled={isProcessingPark || isParked}
              >
                <Grid3X3 className="w-5 h-5" />
              </Button>

              {onTransfer && (
                <Button
                  variant="outline"
                  size="icon"
                  className="w-14 h-14 rounded-full border-2 bg-background/80 border-border hover:bg-accent hover:border-primary/30 transition-all duration-300 shadow-lg"
                  onClick={onTransfer}
                  disabled={isProcessingPark || isParked}
                >
                  <PhoneForwarded className="w-5 h-5" />
                </Button>
              )}

              <Button
                variant="outline"
                size="icon"
                className="w-14 h-14 rounded-full border-2 bg-background/80 border-border hover:bg-accent hover:border-primary/30 transition-all duration-300 shadow-lg"
                onClick={handleMinimize}
                disabled={isProcessingPark}
              >
                <Minimize2 className="w-5 h-5" />
              </Button>

              <Button
                variant="destructive"
                size="icon"
                className="w-16 h-16 rounded-full shadow-2xl transition-all duration-300 bg-gradient-to-br from-destructive to-destructive/80 hover:from-destructive/90 hover:to-destructive/70 ring-4 ring-destructive/20"
                onClick={handleEndCall}
                disabled={isProcessingPark}
              >
                <PhoneOff className="w-7 h-7" />
              </Button>
            </div>

            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
              <div className={`w-2 h-2 rounded-full animate-pulse ${isParked ? "bg-yellow-500" : "bg-primary"}`} />
              <p className="text-sm text-muted-foreground font-medium">
                {isParked ? "🎵 Cliente em espera" : "Chamada em andamento"}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ActiveCallModal;
